import api, { unwrap } from './client';
import type { ApiResponse } from '@/types/api';

export interface WarehouseResponse {
  id: number;
  code: string;
  name: string;
  storeId?: number;
  warehouseType?: string;
  isTransit?: boolean;
  isDefault?: boolean;
}

export interface WarehouseDashboardResponse {
  warehouseCount: number;
  openPutawayTasks: number;
  openPickLists: number;
  openTransfers: number;
  movementsToday: number;
}

export interface WmPutawayTask {
  id: number;
  taskCode: string;
  warehouseId: number;
  productId: number;
  quantity: number;
  recommendedBinId?: number;
  statusCode: string;
}

export interface WmPickListResponse {
  id: number;
  pickCode: string;
  warehouseId: number;
  statusCode: string;
  tasks: { id: number; productId: number; quantity: number; statusCode: string }[];
}

export interface WmTransfer {
  id: number;
  transferCode: string;
  statusCode: string;
  fromWarehouseId?: number;
  toWarehouseId?: number;
}

export interface WmWave {
  id: number;
  waveCode: string;
  warehouseId: number;
  statusCode: string;
  priority?: number;
  releasedAt?: string;
}

export interface WmDockAppointment {
  id: number;
  dockId: number;
  warehouseId: number;
  carrierName?: string;
  scheduledStart: string;
  scheduledEnd: string;
  statusCode: string;
}

export interface WmCrossDockStaging {
  id: number;
  warehouseId: number;
  productId: number;
  quantity: number;
  statusCode: string;
  receiptLineId?: number;
  outboundPickTaskId?: number;
}

export interface WmCycleCount {
  id: number;
  countCode: string;
  warehouseId: number;
  statusCode: string;
  frequency?: string;
  scheduledDate?: string;
}

export interface WmCycleCountLine {
  id: number;
  cycleCountId: number;
  productId: number;
  binId?: number;
  systemQuantity: number;
  physicalQuantity?: number;
  varianceQuantity?: number;
  statusCode: string;
  approvalStatus?: string;
}

export interface WmLaborMetricRow {
  userId: number;
  scanCount: number;
  picksCompleted: number;
  putawaysCompleted: number;
}

export interface WarehouseZone {
  id: number;
  warehouseId: number;
  zoneCode: string;
  zoneName: string;
}

export interface WarehouseBin {
  id: number;
  warehouseId?: number;
  binCode: string;
  binType?: string;
  capacity?: number;
}

export const warehouseApi = {
  listWarehouses: () => unwrap(api.get<ApiResponse<WarehouseResponse[]>>('/v1/warehouses')),

  createWarehouse: (body: {
    code: string;
    name: string;
    storeId?: number;
    warehouseType?: string;
    isTransit?: boolean;
  }) => unwrap(api.post<ApiResponse<WarehouseResponse>>('/v1/warehouses', body)),

  updateWarehouse: (
    id: number,
    body: {
      code: string;
      name: string;
      storeId?: number;
      warehouseType?: string;
      isTransit?: boolean;
    },
  ) => unwrap(api.put<ApiResponse<WarehouseResponse>>(`/v1/warehouses/${id}`, body)),

  deleteWarehouse: (id: number) => unwrap(api.delete<ApiResponse<void>>(`/v1/warehouses/${id}`)),

  archiveWarehouse: (id: number) =>
    unwrap(api.post<ApiResponse<WarehouseResponse>>(`/v1/warehouses/${id}/archive`)),

  dashboard: () =>
    unwrap(api.get<ApiResponse<WarehouseDashboardResponse>>('/v1/warehouse/dashboard')),

  listOpenPutaway: () =>
    unwrap(api.get<ApiResponse<WmPutawayTask[]>>('/v1/warehouse/putaway/open')),

  confirmPutaway: (id: number, targetBinId?: number) =>
    unwrap(
      api.post<ApiResponse<WmPutawayTask>>(`/v1/warehouse/putaway/${id}/confirm`, {
        targetBinId,
      }),
    ),

  listZones: (warehouseId: number) =>
    unwrap(
      api.get<ApiResponse<WarehouseZone[]>>(`/v1/warehouse/warehouses/${warehouseId}/zones`),
    ),

  createZone: (warehouseId: number, body: { zoneCode: string; zoneName: string }) =>
    unwrap(
      api.post<ApiResponse<WarehouseZone>>(
        `/v1/warehouse/warehouses/${warehouseId}/zones`,
        body,
      ),
    ),

  listBins: (warehouseId: number) =>
    unwrap(
      api.get<ApiResponse<WarehouseBin[]>>(`/v1/warehouse/warehouses/${warehouseId}/bins`),
    ),

  createBin: (
    warehouseId: number,
    body: { binCode: string; binType?: string; capacity?: number; shelfId?: number },
  ) =>
    unwrap(
      api.post<ApiResponse<WarehouseBin>>(
        `/v1/warehouse/warehouses/${warehouseId}/bins`,
        body,
      ),
    ),

  scan: (body: {
    barcodeValue: string;
    operation: string;
    referenceType?: string;
    referenceId?: number;
    deviceId?: string;
  }) => unwrap(api.post<ApiResponse<Record<string, unknown>>>('/v1/warehouse/scan', body)),

  listOpenPicks: () =>
    unwrap(api.get<ApiResponse<WmPickListResponse[]>>('/v1/warehouse/picks/open')),

  createPick: (body: {
    warehouseId: number;
    storeId?: number;
    pickStrategy?: string;
    referenceType?: string;
    referenceId?: number;
    lines: { productId: number; quantity: number; fromBinId?: number }[];
  }) => unwrap(api.post<ApiResponse<WmPickListResponse>>('/v1/warehouse/picks', body)),

  confirmPick: (taskId: number, body: { pickedQuantity: number; fromBinId?: number }) =>
    unwrap(
      api.post<ApiResponse<unknown>>(`/v1/warehouse/picks/tasks/${taskId}/confirm`, body),
    ),

  createPack: (pickListId: number) =>
    unwrap(api.post<ApiResponse<{ id: number }>>(`/v1/warehouse/picks/${pickListId}/pack`)),

  completePack: (packId: number, body?: { notes?: string }) =>
    unwrap(api.post<ApiResponse<unknown>>(`/v1/warehouse/pack/${packId}/complete`, body ?? {})),

  createDispatch: (packId: number) =>
    unwrap(api.post<ApiResponse<{ id: number }>>(`/v1/warehouse/pack/${packId}/dispatch`)),

  completeDispatch: (dispatchId: number, carrierRef?: string) =>
    unwrap(
      api.post<ApiResponse<unknown>>(
        `/v1/warehouse/dispatch/${dispatchId}/complete`,
        null,
        { params: carrierRef ? { carrierRef } : undefined },
      ),
    ),

  listOpenTransfers: () =>
    unwrap(api.get<ApiResponse<WmTransfer[]>>('/v1/warehouse/transfers/open')),

  createTransfer: (body: {
    fromWarehouseId: number;
    toWarehouseId: number;
    notes?: string;
    lines: { productId: number; quantity: number }[];
  }) => unwrap(api.post<ApiResponse<WmTransfer>>('/v1/warehouse/transfers', body)),

  approveTransfer: (id: number) =>
    unwrap(api.post<ApiResponse<WmTransfer>>(`/v1/warehouse/transfers/${id}/approve`)),

  dispatchTransfer: (id: number) =>
    unwrap(api.post<ApiResponse<WmTransfer>>(`/v1/warehouse/transfers/${id}/dispatch`)),

  receiveTransfer: (id: number) =>
    unwrap(api.post<ApiResponse<WmTransfer>>(`/v1/warehouse/transfers/${id}/receive`)),

  listActiveCycleCounts: () =>
    unwrap(api.get<ApiResponse<WmCycleCount[]>>('/v1/warehouse/cycle-counts/active')),

  listCycleCountLines: (cycleCountId: number) =>
    unwrap(
      api.get<ApiResponse<WmCycleCountLine[]>>(
        `/v1/warehouse/cycle-counts/${cycleCountId}/lines`,
      ),
    ),

  createCycleCount: (body: {
    warehouseId: number;
    zoneId?: number;
    frequency?: string;
    scheduledDate?: string;
    lines?: { productId: number; binId?: number }[];
  }) => unwrap(api.post<ApiResponse<WmCycleCount>>('/v1/warehouse/cycle-counts', body)),

  startCycleCount: (id: number) =>
    unwrap(api.post<ApiResponse<WmCycleCount>>(`/v1/warehouse/cycle-counts/${id}/start`)),

  recordCycleCount: (lineId: number, physicalQuantity: number) =>
    unwrap(
      api.post<ApiResponse<WmCycleCountLine>>(
        `/v1/warehouse/cycle-counts/lines/${lineId}/record`,
        { physicalQuantity },
      ),
    ),

  approveCycleVariance: (lineId: number) =>
    unwrap(
      api.post<ApiResponse<WmCycleCountLine>>(
        `/v1/warehouse/cycle-counts/lines/${lineId}/approve-variance`,
      ),
    ),

  completeCycleCount: (id: number) =>
    unwrap(api.post<ApiResponse<WmCycleCount>>(`/v1/warehouse/cycle-counts/${id}/complete`)),

  listWaves: () => unwrap(api.get<ApiResponse<WmWave[]>>('/v1/warehouse/waves')),

  createWave: (body: { warehouseId: number; pickListIds: number[] }) =>
    unwrap(api.post<ApiResponse<WmWave>>('/v1/warehouse/waves', body)),

  releaseWave: (id: number) =>
    unwrap(api.post<ApiResponse<WmWave>>(`/v1/warehouse/waves/${id}/release`)),

  listDockAppointments: () =>
    unwrap(api.get<ApiResponse<WmDockAppointment[]>>('/v1/warehouse/dock-appointments')),

  scheduleDock: (body: {
    dockId: number;
    warehouseId: number;
    carrierName?: string;
    scheduledStart: string;
    scheduledEnd: string;
  }) => unwrap(api.post<ApiResponse<WmDockAppointment>>('/v1/warehouse/dock-appointments', body)),

  checkInDock: (id: number, receiptId?: number) =>
    unwrap(
      api.post<ApiResponse<WmDockAppointment>>(`/v1/warehouse/dock-appointments/${id}/check-in`, {
        receiptId,
      }),
    ),

  listCrossDock: () =>
    unwrap(api.get<ApiResponse<WmCrossDockStaging[]>>('/v1/warehouse/cross-dock')),

  stageCrossDock: (body: {
    warehouseId: number;
    productId: number;
    quantity: number;
    receiptLineId?: number;
    outboundPickTaskId?: number;
  }) => unwrap(api.post<ApiResponse<WmCrossDockStaging>>('/v1/warehouse/cross-dock', body)),

  completeCrossDock: (id: number) =>
    unwrap(api.post<ApiResponse<WmCrossDockStaging>>(`/v1/warehouse/cross-dock/${id}/complete`)),

  laborMetrics: (day?: string) =>
    unwrap(
      api.get<ApiResponse<WmLaborMetricRow[]>>('/v1/warehouse/labor-metrics', {
        params: day ? { day } : undefined,
      }),
    ),
};
